import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [playback]
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Animation | undefined} */
  #animation;
  #transition = 0;
  #events = new AbortController();
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #editing = false;

  static get observedAttributes() {
    return ['autoplay'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.addEventListener('mouseenter', this.suspend, options);
    this.addEventListener('mouseleave', this.resume, options);
    this.addEventListener('focusin', this.suspend, options);
    this.addEventListener('focusout', this.#handleFocusOut, options);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, options);
    this.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, options);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, options);
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange, options);
    if (this.#reducedMotion.matches) this.paused = true;
    this.#select(this.#current, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#events.abort();
    this.suspend();
    this.#cancelAnimation();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#select(this.#current, false);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.resume();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  play() {
    this.paused = false;
    this.resume();
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  togglePlayback() {
    if (this.paused) this.play();
    else this.pause();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
    this.#updateControls();
  }

  suspend() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
  }

  resume() {
    this.suspend();
    if (
      !this.isConnected || !this.autoplay || this.paused || this.#editing ||
      this.#animation || document.hidden || this.matches(':hover, :focus-within') ||
      (this.refs.slides?.length ?? 0) < 2
    ) return;
    this.#timer = setTimeout(() => this.next(), this.autoplayInterval);
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const value = Number(this.getAttribute('autoplay'));
    return Number.isFinite(value) && value > 0 ? value * 1000 : undefined;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#select(current);
  }

  #cancelAnimation() {
    this.#transition += 1;
    this.#animation?.cancel();
    this.#animation = undefined;
  }

  /** @param {number} index @param {boolean} [animate] */
  async #select(index, animate = true) {
    this.suspend();
    this.#cancelAnimation();
    const transition = this.#transition;
    const slides = this.refs.slides ?? [];
    this.#updateControls();
    if (!slides.length) return;
    this.#current = ((index % slides.length) + slides.length) % slides.length;
    const incoming = slides[this.#current];
    if (!incoming) return;
    const outgoing = slides.find(slide => slide.getAttribute('aria-hidden') === 'false');
    const animated = animate && !this.#reducedMotion.matches && outgoing && outgoing !== incoming;

    try {
      if (animated) {
        this.#animation = outgoing.animate(
          [{ transform: 'translateX(0)' }, { transform: 'translateX(-100%)' }],
          { duration: 350, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
        );
        await this.#animation.finished;
        if (transition !== this.#transition) return;
      }

      for (const slide of slides) {
        const hidden = slide !== incoming;
        slide.setAttribute('aria-hidden', String(hidden));
        slide.inert = hidden;
      }
      this.#animation?.cancel();
      this.#animation = undefined;

      if (animated) {
        this.#animation = incoming.animate(
          [{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }],
          { duration: 450, easing: 'cubic-bezier(0, 0, 0.2, 1)' }
        );
        await this.#animation.finished;
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) throw error;
    } finally {
      if (transition === this.#transition) {
        this.#animation = undefined;
        this.resume();
      }
    }
  }

  #updateControls() {
    this.toggleAttribute('data-multiple', (this.refs.slides?.length ?? 0) > 1);
    this.refs.slideshowContainer?.setAttribute('aria-live', this.paused ? 'polite' : 'off');
    const button = this.refs.playback;
    if (button) button.setAttribute('aria-label', (this.paused ? button.dataset.playLabel : button.dataset.pauseLabel) ?? '');
  }

  #handleFocusOut = () => queueMicrotask(() => this.resume());
  #handleVisibilityChange = () => document.hidden ? this.suspend() : this.resume();
  #handleMotionChange = () => {
    if (this.#reducedMotion.matches) this.pause();
    this.#select(this.#current, false);
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const slide = event.target.closest('.announcement-bar__slide');
    const index = (this.refs.slides ?? []).findIndex(item => item === slide);
    if (index < 0) return;
    this.#editing = true;
    this.#select(index, false);
  };

  #handleBlockDeselect = () => {
    this.#editing = false;
    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
